'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import Link from 'next/link'
import { useCallback } from 'react'

import { useUserNotifications } from '@/hooks/queries/notifications'
import { cn } from '@/lib/cn'
import { notificationsService } from '@/services/http/notificationsService'
import type { Notification } from '@/services/http/notificationsService/getUserNotifications'
import { getNotificationActionLabel } from '@/utils/notifications/getNotificationActionLabel'
import { generateRoutePath } from '@/utils/routes/generateRoutePath'
import { stripHTML } from '@/utils/sanitize'
import { Button } from '../button'
import { MentionView } from '../mentionTextarea'
import { ScrollArea } from '../scrollArea'
import { Separator } from '../separator'
import { useToast } from '../toast'
import { UserAvatar } from '../userAvatar'
import { NotificationPopoverContentSkeleton } from './skeleton'

const NotificationPopoverContent = () => {
  const { toast } = useToast()

  const { notifications, isLoading } = useUserNotifications({
    query: {
      page: 1,
      limit: 5,
    },
  })

  const queryClient = useQueryClient()

  const { mutateAsync: mutateHandleReadAllUserNotifications } = useMutation({
    mutationFn: notificationsService.readAllUserNotifications,
  })

  const handleReadAllNotifications = useCallback(async () => {
    try {
      await mutateHandleReadAllUserNotifications()

      queryClient.invalidateQueries({
        queryKey: ['notifications'],
        exact: false,
      })

      toast({
        title: 'Notificações marcadas como lidas',
        variant: 'success',
      })
    } catch {
      toast({
        title: 'Erro ao marcar notificações como lidas',
        variant: 'destructive',
      })
    }
  }, [mutateHandleReadAllUserNotifications, queryClient, toast])

  const { mutateAsync: mutateHandleReadUserNotification } = useMutation({
    mutationFn: notificationsService.readUserNotification,
  })

  const handleReadNotification = useCallback(
    async (notification: Notification) => {
      try {
        await mutateHandleReadUserNotification(notification.id)

        queryClient.invalidateQueries({
          queryKey: ['notifications'],
          exact: false,
        })

        toast({
          title: 'Notificação marcada como lida',
          variant: 'success',
        })
      } catch {
        toast({
          title: 'Erro ao marcar notificação como lida',
          variant: 'destructive',
        })
      }
    },
    [mutateHandleReadUserNotification, queryClient, toast],
  )

  if (isLoading) {
    return <NotificationPopoverContentSkeleton />
  }

  return (
    <section className="flex flex-col">
      <div className="mb-4 flex items-center justify-between p-4 pb-0">
        <span className="text-base font-semibold text-system-text-primary">
          Notificações
        </span>
        <Button
          variant="link"
          size="link"
          className="text-xs underline-offset-2"
          onClick={handleReadAllNotifications}
        >
          Marcar tudo como lido
        </Button>
      </div>

      <ScrollArea>
        <div className="flex max-h-72 flex-col">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={cn(
                'flex flex-col gap-3 p-4 border-b border-b-system-border-secondary',
                !notification.isRead && 'bg-system-text-link/5',
              )}
            >
              <div className="flex items-start gap-3">
                {notification.originUser?.id && (
                  <UserAvatar
                    src={notification.originUser?.image}
                    name={notification.originUser?.name ?? ''}
                    className="size-8"
                  />
                )}

                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-2">
                    <span className="text-xs font-semibold text-system-text-primary">
                      {stripHTML(notification.title)}
                    </span>
                    {notification?.description && (
                      <p className="flex items-center text-xs font-medium text-system-text-secondary">
                        {notification.description.endsWith('.') ? (
                          <span>
                            {stripHTML(notification.description)}{' '}
                            {notification.link && (
                              <Button
                                variant="link"
                                size="link"
                                className="text-xs"
                                asChild
                              >
                                <Link href={notification.link}>
                                  {getNotificationActionLabel(
                                    notification.type,
                                  )}
                                </Link>
                              </Button>
                            )}
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <MentionView
                              content={stripHTML(notification.description)}
                            />
                            {notification.link && (
                              <Button
                                variant="link"
                                size="link"
                                className="text-xs"
                                asChild
                              >
                                <Link href={notification.link}>
                                  {getNotificationActionLabel(
                                    notification.type,
                                  )}
                                </Link>
                              </Button>
                            )}
                          </div>
                        )}
                      </p>
                    )}
                  </div>

                  {notification?.comment && (
                    <div className="flex items-center gap-3">
                      <Separator
                        orientation="vertical"
                        className="h-6 w-[2px] bg-system-primary-blue"
                        decorative={false}
                      />
                      <span className="text-xs font-medium text-system-text-secondary">
                        {stripHTML(notification.comment)}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-system-text-secondary">
                      {notification.relativeCreatedAt}
                    </span>

                    <Button
                      type="button"
                      variant="link"
                      size="link"
                      className="text-xs underline-offset-2"
                      onClick={() => handleReadNotification(notification)}
                    >
                      Marcar como lida
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="flex items-center justify-center rounded-b-md bg-system-grey-light py-5">
        <Button variant="link" size="link" asChild>
          <Link href={generateRoutePath('notifications')}>
            Ver todas as notificações
          </Link>
        </Button>
      </div>
    </section>
  )
}
NotificationPopoverContent.displayName = 'NotificationPopoverContent'

export { NotificationPopoverContent }
